/**
 * Sandbox-mode auth primitives for the consent PoC.
 *
 * In sandbox mode the SDK talks to the consent-server with a pasted static
 * token and an explicit test subject, bypassing the JWT entirely. Org/workspace
 * UUIDs come from options (they still build the `/public/organisations/{org}/
 * workspaces/{ws}/...` URL paths) instead of being decoded from a token.
 *
 * Auth is the single `X-Consent-Token` header carrying the static token; its
 * presence alone marks the request as `environment=test`. There is no subject
 * header. The acting identity (the test subject) rides the request's own
 * payload: query params (`primary_email` / `primary_mobile`) on GET/query reads,
 * and the body on POSTs. The server normalizes and namespaces it `test::`
 * server-side. Call sites resolve the credential via {@link buildAuthHeaders}
 * and the identity via {@link sandboxQueryIdentity} / {@link sandboxBodyIdentity}.
 *
 * This file exists so every JWT-decode / Authorization site resolves its
 * org/workspace UUIDs and builds its auth header through one place, keeping
 * sandbox vs. JWT branching from drifting across the fetch sites.
 */
import { decodeTokenSafely } from "../core/jwt-decode";
import type {
  ResolvedRequestAuth,
  SandboxAuth,
  SandboxIdentityPayload,
  SandboxOptions,
} from "./types";

/** HTTP header carrying the static sandbox token (the credential). */
export const SANDBOX_TOKEN_HEADER = "X-Consent-Token";

/**
 * Resolve a {@link SandboxAuth} from sandbox options, or null when sandbox mode
 * is not active (no token).
 *
 * @throws Error when a sandbox token is provided but org/workspace or the acting
 *   identity are incomplete — sandbox mode cannot build URLs or identify the
 *   acting principal without them.
 */
export const resolveSandboxAuth = (
  options: SandboxOptions
): SandboxAuth | null => {
  const token = options.token?.trim();
  if (!token) {
    return null;
  }

  const organisationUuid = options.organisationUuid?.trim();
  const workspaceUuid = options.workspaceUuid?.trim();
  const ucic = options.sandboxUcic?.trim();
  const email = options.sandboxEmail?.trim();
  const mobile = options.sandboxMobile?.trim();

  if (!organisationUuid || !workspaceUuid) {
    throw new Error(
      "Sandbox mode requires organisationUuid and workspaceUuid"
    );
  }
  if (!ucic && !email && !mobile) {
    throw new Error("Sandbox mode requires a UCIC, email, or mobile");
  }

  return { token, ucic, email, mobile, organisationUuid, workspaceUuid };
};

/**
 * Resolve org/workspace UUIDs plus the sandbox context for an authenticated
 * request. In sandbox mode these come from options; otherwise they are decoded
 * from the JWT (preserving the pre-sandbox error messages exactly).
 *
 * @throws Error when neither a valid sandbox config nor a decodable token with
 *   org/workspace UUIDs is available.
 */
export const resolveRequestAuth = (
  accessToken: string | undefined,
  options: SandboxOptions
): ResolvedRequestAuth => {
  const sandbox = resolveSandboxAuth(options);
  if (sandbox) {
    return {
      organisationUuid: sandbox.organisationUuid,
      workspaceUuid: sandbox.workspaceUuid,
      sandbox,
    };
  }

  const decodedToken = accessToken ? decodeTokenSafely(accessToken) : null;
  if (!decodedToken) {
    throw new Error("Invalid access token");
  }
  const { organisation_uuid, workspace_uuid } = decodedToken;
  if (!organisation_uuid || !workspace_uuid) {
    throw new Error("Invalid token: missing organization or workspace UUID");
  }
  return {
    organisationUuid: organisation_uuid,
    workspaceUuid: workspace_uuid,
    sandbox: null,
  };
};

/**
 * Build the request headers for an authenticated call. In sandbox mode this is
 * the single `X-Consent-Token` credential (its presence marks the request as
 * `environment=test`); otherwise the standard `Authorization: Bearer`. There is
 * no subject header — the acting identity travels in the request's own payload
 * (see {@link sandboxQueryIdentity} for reads, {@link sandboxBodyIdentity} for
 * POSTs).
 */
export const buildAuthHeaders = (
  accessToken: string | undefined,
  sandbox: SandboxAuth | null
): Record<string, string> => {
  if (sandbox) {
    return { [SANDBOX_TOKEN_HEADER]: sandbox.token };
  }
  return { Authorization: `Bearer ${accessToken}` };
};

/**
 * The identity payload for the sandbox acting principal — every identifier the
 * host supplied (`org_user_id` / `primary_email` / `primary_mobile`), mirroring
 * a live JWT's `user_data`. The server keys the principal by precedence (UCIC >
 * email > mobile) and preserves the rest as the display-only sandbox contact, so
 * the contact shows on the dashboard/receipt just as it does for a live user.
 * Merge this into a JSON request body (POST) or into query params (GET).
 */
export const sandboxBodyIdentity = (
  sandbox: SandboxAuth
): SandboxIdentityPayload => {
  const payload: SandboxIdentityPayload = {};
  if (sandbox.ucic) {
    payload.org_user_id = sandbox.ucic;
  }
  if (sandbox.email) {
    payload.primary_email = sandbox.email;
  }
  if (sandbox.mobile) {
    payload.primary_mobile = sandbox.mobile;
  }
  return payload;
};

/**
 * The identity query fragment for GET/query reads — the same full identity as
 * {@link sandboxBodyIdentity}. Append these to the request's query string so the
 * server can resolve the acting test principal (it reads `org_user_id` /
 * `primary_email` / `primary_mobile` query params on sandbox reads).
 */
export const sandboxQueryIdentity = (
  sandbox: SandboxAuth
): SandboxIdentityPayload => sandboxBodyIdentity(sandbox);
